"use client"

import type { ReactNode } from "react"
import { ArrowUpDown, Briefcase, Check, CheckCircle2, Clock, X } from "lucide-react"

import { TextAction } from "@/components/graphite/text-action"
import { CareerAxis, CareerTrack } from "@/components/search/career-track"
import { TINT, avatarHex, stageHex, stageTint, toneStyle } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { Axis, Bar } from "@/lib/career-track"

/**
 * Search results as careers on a shared axis — canvas 27a and 27b, with 30a and
 * 30c as the phone.
 *
 * Turn 27 folds three earlier passes into one: "Turns 24–26 trimmed the shipped
 * card field by field — out went the headline, the score badge, the skill chips,
 * the insight lines, the years-experience fragment, the tier word and the `new`
 * chip — and what remained was still a list of strings, which is what every ATS
 * ships." So the roles are plotted instead, and the row is four columns read
 * left to right: identity, current role, career, verdict.
 *
 * The rank numeral replaces the score badge deliberately: "nothing needs to
 * assert a tier; the rank numeral on the left says where the match engine
 * placed each candidate, which is the one claim the row can actually stand
 * behind."
 *
 * On the phone the bars are gone. 30's note: "at 132px they showed nothing the
 * dates did not already say, and they cost a column" — so a phone gets the same
 * four roles as a dated ledger.
 */

export type Verdict = "none" | "yes" | "maybe" | "hidden"

export type ResultRowData = {
  candidateId: string
  rank: number
  name: string
  initials: string
  location?: string
  /** "Staff Engineer, Platform · Klarna" */
  currentRole?: string
  bars: Bar[]
  /** The same roles as text, for the phone and for a track with no dates. */
  roles: { key: string; company?: string; range: string; isCurrent: boolean }[]
  verdict: Verdict
  isAdded: boolean
}

const COLUMNS = "grid-cols-[26px_16px_minmax(0,226px)_minmax(0,1fr)_172px]"
const LABEL =
  "text-[9px] font-medium uppercase tracking-[0.16em] text-muted-foreground"

/** The one place a result's verdict is written, at both sizes. */
function VerdictActions({
  row,
  onVerdict,
  large,
  /** The linked-job lookup has not answered, so a Yes has nowhere to land yet. */
  isResolving,
}: {
  row: ResultRowData
  onVerdict: (verdict: Verdict) => void
  large?: boolean
  isResolving?: boolean
}) {
  const button = cn(
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border text-[11.5px] font-medium transition-colors",
    large ? "min-h-11 flex-1 px-3" : "px-[11px] py-1.5",
  )

  /*
    Added is a fact about the job, not a verdict that can be toggled. 27a draws
    an "Undo" beside it and nothing in the API can honour one — no endpoint
    removes a candidate from a pipeline — so the word is not drawn. A control
    that cannot do what it says is worse than an absent one.

    Backend ask: a pipeline remove, and this becomes the artboard.
  */
  const addedPill = (
    <span
      className={cn(button, "border-transparent")}
      style={toneStyle(stageHex("hired"))}
    >
      <CheckCircle2 className="h-3 w-3" />
      {large ? "Added to longlist" : "Added"}
    </span>
  )

  /*
    Hidden comes first, and carries the Added pill with it when both are true.
    Added used to return above this, so a candidate who had been added and then
    hidden came back under "show hidden" as a dimmed row reading "Added" with no
    way to unhide — the one state with no way out of itself.
  */
  if (row.verdict === "hidden") {
    return (
      <div className={cn("flex items-start gap-2", large && "w-full")}>
        {row.isAdded ? (
          addedPill
        ) : (
          <span className={cn(button, "border-border text-muted-foreground")}>
            Hidden
          </span>
        )}
        <TextAction
          tone="teal"
          onClick={() => onVerdict("none")}
          className="pt-1.5 text-[11px]"
        >
          Unhide
        </TextAction>
      </div>
    )
  }

  /*
    Added keeps its Hide. Hiding is a verdict about the *result list* — this
    person is dealt with, stop showing me — and it is independent of the
    pipeline, which nothing here can take them off anyway. Without it an added
    candidate sat in the results for good.
  */
  if (row.isAdded) {
    return (
      <div className={cn("flex items-start gap-2", large && "w-full")}>
        {addedPill}
        <button
          type="button"
          onClick={() => onVerdict("hidden")}
          aria-label={`Hide ${row.name}`}
          className={cn(
            "flex flex-none items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground",
            large ? "min-h-11 w-14" : "h-[26px] w-[29px]",
          )}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    )
  }

  if (row.verdict === "maybe") {
    return (
      <div className={cn("flex items-start gap-2", large && "w-full")}>
        <span className={cn(button, "border-attention/30 bg-attention/10 text-attention")}>
          <Clock className="h-3 w-3" />
          Maybe
        </span>
        <TextAction onClick={() => onVerdict("none")} className="pt-1.5 text-[11px]">
          Clear
        </TextAction>
      </div>
    )
  }

  return (
    <div className={cn("flex items-start justify-end gap-[7px]", large && "w-full")}>
      <button
        type="button"
        onClick={() => onVerdict("yes")}
        disabled={isResolving}
        title={isResolving ? "Finding the job this search feeds…" : undefined}
        className={cn(
          button,
          "border-primary text-brand-ink disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <Check className="h-3 w-3" />
        Yes
      </button>
      <button
        type="button"
        onClick={() => onVerdict("maybe")}
        className={cn(button, "border-border text-muted-foreground hover:text-foreground")}
      >
        Maybe
      </button>
      <button
        type="button"
        onClick={() => onVerdict("hidden")}
        aria-label={`Hide ${row.name}`}
        className={cn(
          "flex flex-none items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground",
          large ? "min-h-11 w-14" : "h-[26px] w-[29px]",
        )}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}

/** The four roles as dated lines — the phone, and any track with no dates. */
function RoleLedger({ roles }: { roles: ResultRowData["roles"] }) {
  if (roles.length === 0) return null
  return (
    <div className="flex flex-col gap-[5px]">
      {roles.map((role) => (
        <div
          key={role.key}
          className="grid grid-cols-[minmax(0,1fr)_76px] items-center gap-[9px]"
        >
          <span
            className={cn(
              "min-w-0 truncate text-[11.5px] leading-[1.5]",
              role.isCurrent ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {role.company}
          </span>
          <span className="text-right font-mono text-[10px] leading-[1.5] text-muted-foreground">
            {role.range}
          </span>
        </div>
      ))}
    </div>
  )
}

function Identity({ row, onOpen }: { row: ResultRowData; onOpen: () => void }) {
  const hex = avatarHex(row.candidateId || row.name)
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <span
        aria-hidden
        className="flex size-[30px] flex-none items-center justify-center rounded-full text-[11px] font-medium"
        style={{ color: hex, backgroundColor: stageTint(hex, TINT.pill) }}
      >
        {row.initials}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <button
          type="button"
          onClick={onOpen}
          className="max-w-full truncate border-b border-dotted border-muted-foreground pb-0.5 text-left text-[13.5px] font-semibold leading-[1.25] transition-colors hover:text-brand-ink"
        >
          {row.name}
        </button>
        {row.location && (
          <span className="truncate text-[11px] leading-[1.3] text-muted-foreground">
            {row.location}
          </span>
        )}
      </span>
    </div>
  )
}

function ResultRow({
  row,
  axis,
  isSelected,
  onToggleSelect,
  onOpen,
  onVerdict,
  striped,
  isResolving,
}: {
  row: ResultRowData
  axis: Axis
  isSelected: boolean
  onToggleSelect: () => void
  onOpen: () => void
  onVerdict: (verdict: Verdict) => void
  striped: boolean
  isResolving?: boolean
}) {
  return (
    <div
      className={cn(
        "grid items-start gap-3.5 border-t px-[18px] py-[13px]",
        COLUMNS,
        striped && "bg-foreground/[0.02]",
        isSelected && "shadow-[inset_2px_0_0_var(--primary)]",
        row.verdict === "hidden" && "opacity-45",
      )}
    >
      <div className="pt-[3px] font-mono text-[10px] leading-[1.6] text-muted-foreground">
        {String(row.rank).padStart(2, "0")}
      </div>

      <div className="pt-0.5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          aria-label={`Select ${row.name}`}
          className="size-[15px] accent-[var(--primary)]"
        />
      </div>

      <Identity row={row} onOpen={onOpen} />

      <div className="flex min-w-0 flex-col gap-1.5 pt-px">
        {row.currentRole && (
          <div className="truncate text-[12.5px] leading-[1.3] text-foreground/80">
            {row.currentRole}
          </div>
        )}
        {row.bars.length > 0 ? (
          <CareerTrack bars={row.bars} axis={axis} className="mt-0.5" />
        ) : (
          <span className="font-mono text-[10.5px] text-muted-foreground">
            no dated roles on record
          </span>
        )}
      </div>

      <VerdictActions row={row} onVerdict={onVerdict} isResolving={isResolving} />
    </div>
  )
}

/** 30a: rank, name and city on one line, the roles as a ledger, thumb-sized verdicts. */
function ResultCard({
  row,
  isSelected,
  onToggleSelect,
  onOpen,
  onVerdict,
  isResolving,
}: {
  row: ResultRowData
  isSelected: boolean
  onToggleSelect: () => void
  onOpen: () => void
  onVerdict: (verdict: Verdict) => void
  isResolving?: boolean
}) {
  return (
    <div
      className={cn(
        "border-b px-4 pb-4 pt-[15px]",
        isSelected && "shadow-[inset_3px_0_0_var(--primary)]",
        row.verdict === "hidden" && "opacity-45",
      )}
    >
      <div className="flex items-baseline gap-2.5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          aria-label={`Select ${row.name}`}
          className="size-[15px] flex-none accent-[var(--primary)]"
        />
        <span className="font-mono text-[10px] leading-[1.4] text-muted-foreground">
          {String(row.rank).padStart(2, "0")}
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 truncate text-left text-[15px] font-semibold leading-[1.25]"
        >
          {row.name}
        </button>
        {row.location && (
          <span className="ml-auto flex-none truncate text-[11px] leading-[1.4] text-muted-foreground">
            {row.location}
          </span>
        )}
      </div>

      {row.currentRole && (
        <div className="mb-[11px] ml-[21px] mt-0.5 truncate text-[12.5px] leading-[1.4] text-foreground/80">
          {row.currentRole}
        </div>
      )}

      <div className="ml-[21px]">
        <RoleLedger roles={row.roles} />
        <div className="mt-3 flex gap-2">
          <VerdictActions
            row={row}
            onVerdict={onVerdict}
            isResolving={isResolving}
            large
          />
        </div>
      </div>
    </div>
  )
}

/** "24 results · 4 maybe · 1 hidden", and whatever the page can sort by. */
export function ResultsHeader({
  count,
  maybeCount,
  hiddenCount,
  showHidden,
  onToggleHidden,
}: {
  count: number
  maybeCount: number
  hiddenCount: number
  showHidden: boolean
  onToggleHidden: () => void
}) {
  return (
    <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-2 border-b bg-chrome px-4 py-3 @2xl:px-[18px]">
      <span className="text-[13px] font-semibold leading-none">
        {count} result{count === 1 ? "" : "s"}
      </span>
      {/*
        27a reads "of 240 · ranked by match". The endpoint pages and says only
        whether another page exists — it carries no total — so the line claims
        the ranking, which is true of every page, and not a number nobody sent.
      */}
      <span className="font-mono text-[11px] leading-none text-muted-foreground">
        ranked by match
      </span>
      <span className="ml-auto flex items-center gap-4">
        {maybeCount > 0 && (
          <span className="font-mono text-[11px] leading-none text-attention">
            {maybeCount} maybe
          </span>
        )}
        {hiddenCount > 0 && (
          <TextAction
            onClick={onToggleHidden}
            className="font-mono text-[11px] leading-none"
          >
            <ArrowUpDown className="h-3 w-3" />
            {showHidden ? `hiding none of ${hiddenCount}` : `${hiddenCount} hidden`}
          </TextAction>
        )}
      </span>
    </div>
  )
}

/**
 * 27a's second bar: the job every Yes lands in.
 *
 * The artboard puts a "Change" beside it. `PATCH /jobs/{id}/associate-search`
 * only associates a job "that has no existing association", and nothing clears
 * one — so changing the target is not something the product can do. Offering it
 * would link a *second* job to the same search, after which which job a Yes
 * lands on is whichever the list happens to return first. The control is
 * therefore only drawn while there is nothing linked yet.
 *
 * Backend ask: a way to clear or move a job's search association.
 */
export function AddingToBar({
  jobTitle,
  stage = "longlist",
  /**
   * The lookup is two queries deep — the saved search, then the job pointing at
   * it — so until both have answered, "no job linked" is a guess. And it is the
   * dangerous direction to guess in: linking is one-way, so offering it too
   * early can attach a second job to a search that already has one.
   */
  isResolving,
  onLink,
}: {
  jobTitle?: string
  stage?: string
  isResolving?: boolean
  onLink: () => void
}) {
  return (
    <div className="flex flex-none items-center gap-2 border-b bg-background px-4 py-2.5 @2xl:px-[18px]">
      <Briefcase className="h-3 w-3 flex-none" style={{ color: stageHex("shortlist") }} />
      <span className="min-w-0 flex-1 truncate text-[11.5px] leading-[1.35] text-muted-foreground">
        {jobTitle ? (
          <>
            Adding to <span className="font-medium text-foreground">{jobTitle}</span>
            <span className="hidden @2xl:inline"> · {stage}</span>
          </>
        ) : isResolving ? (
          "Looking for the job this search feeds…"
        ) : (
          "No job linked to this search yet"
        )}
      </span>
      {!jobTitle && !isResolving && (
        <TextAction tone="teal" onClick={onLink} className="flex-none text-[11.5px]">
          Link a job
        </TextAction>
      )}
    </div>
  )
}

export function ResultsTable({
  rows,
  axis,
  selected,
  onToggleSelect,
  onOpen,
  onVerdict,
  empty,
  isResolving,
}: {
  rows: ResultRowData[]
  axis: Axis
  selected: string[]
  onToggleSelect: (candidateId: string) => void
  onOpen: (candidateId: string) => void
  onVerdict: (candidateId: string, verdict: Verdict) => void
  empty?: ReactNode
  /** Passed down so a Yes cannot fire before the linked job is known. */
  isResolving?: boolean
}) {
  if (rows.length === 0) return <>{empty}</>

  return (
    <>
      {/* The scale, drawn once over the column the tracks live in. */}
      <div
        className={cn(
          "hidden flex-none items-start gap-3.5 border-b bg-chrome px-[18px] pb-[7px] pt-2 @3xl:grid",
          COLUMNS,
        )}
      >
        <div />
        <div />
        <div className={LABEL}>Candidate</div>
        <CareerAxis axis={axis} />
        <div className={cn(LABEL, "text-right")}>Verdict</div>
      </div>

      <div className="hidden @3xl:block">
        {rows.map((row, index) => (
          <ResultRow
            key={row.candidateId}
            row={row}
            axis={axis}
            striped={index % 2 === 1}
            isSelected={selected.includes(row.candidateId)}
            onToggleSelect={() => onToggleSelect(row.candidateId)}
            onOpen={() => onOpen(row.candidateId)}
            onVerdict={(verdict) => onVerdict(row.candidateId, verdict)}
            isResolving={isResolving}
          />
        ))}
      </div>

      <div className="@3xl:hidden">
        {rows.map((row) => (
          <ResultCard
            key={row.candidateId}
            row={row}
            isSelected={selected.includes(row.candidateId)}
            onToggleSelect={() => onToggleSelect(row.candidateId)}
            onOpen={() => onOpen(row.candidateId)}
            onVerdict={(verdict) => onVerdict(row.candidateId, verdict)}
            isResolving={isResolving}
          />
        ))}
      </div>
    </>
  )
}
